'use client'
import React from 'react'

import { useState } from 'react'
import {
  ChevronRight,
  ChevronDown,
  File,
  FileText,
  Folder,
  FolderOpen,
  Trash2,
  Pencil,
  FileCode,
  FileJson,
  FileType,
  ImageIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FileNode } from '@/lib/file-store'
import { useFileStore } from '@/lib/file-store'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { Input } from '@/components/ui/input'

const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
      return <FileCode className="h-4 w-4 text-yellow-400" />
    case 'json':
      return <FileJson className="h-4 w-4 text-yellow-600" />
    case 'css':
    case 'scss':
    case 'sass':
      return <FileType className="h-4 w-4 text-blue-400" />
    case 'html':
      return <FileType className="h-4 w-4 text-orange-400" />
    case 'md':
    case 'mdx':
      return <FileText className="h-4 w-4 text-blue-300" />
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
      return <ImageIcon className="h-4 w-4 text-green-400" />
    default:
      return <File className="h-4 w-4 text-muted-foreground" />
  }
}

interface FileTreeItemProps {
  node: FileNode
  depth: number
}

function FileTreeItem({ node, depth }: FileTreeItemProps) {
  const { openFile, toggleFolder, deleteFile, renameFile, activeFile } = useFileStore()
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState(node.name)

  const handleClick = () => {
    if (node.type === 'folder') {
      toggleFolder(node.path)
    } else {
      openFile(node.path)
    }
  }

  const handleRename = () => {
    if (newName && newName !== node.name) {
      const pathParts = node.path.split('/')
      pathParts[pathParts.length - 1] = newName
      renameFile(node.path, pathParts.join('/'))
    }
    setIsRenaming(false)
  }

  const isActive = activeFile === node.path

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div>
          <div
            className={cn(
              'flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-sidebar-accent rounded-sm group transition-colors',
              isActive && 'bg-sidebar-accent text-sidebar-accent-foreground'
            )}
            style={{ paddingLeft: `${depth * 12 + 8}px` }}
            onClick={handleClick}
          >
            {node.type === 'folder' && (
              <span className="flex-shrink-0">
                {node.isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </span>
            )}
            <span className="flex-shrink-0">
              {node.type === 'folder' ? (
                node.isOpen ? (
                  <FolderOpen className="h-4 w-4 text-primary" />
                ) : (
                  <Folder className="h-4 w-4 text-primary" />
                )
              ) : (
                getFileIcon(node.name)
              )}
            </span>
            {isRenaming ? (
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={handleRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRename()
                  if (e.key === 'Escape') setIsRenaming(false)
                }}
                className="h-5 py-0 px-1 text-sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="truncate text-sm">{node.name}</span>
            )}
          </div>
          {node.type === 'folder' && node.isOpen && node.children && (
            <div>
              {node.children.map((child) => (
                <FileTreeItem key={child.path} node={child} depth={depth + 1} />
              ))}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => setIsRenaming(true)}>
          <Pencil className="h-4 w-4 mr-2" />
          Rename
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => deleteFile(node.path)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

export function FileTree() {
  const { files } = useFileStore()

  if (files.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        No files yet. Upload a folder or create new files.
      </div>
    )
  }

  return (
    <div className="py-2">
      {files.map((node) => (
        <FileTreeItem key={node.path} node={node} depth={0} />
      ))}
    </div>
  )
}
